import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

import Hls from 'hls.js';

interface UseHlsRewindPlayerOptions {
  /** HLS playlist URL (`hlsRecallUri`). */
  url: string;
  /** When false, the HLS player is torn down and the element released for the live player. */
  enabled: boolean;
  /** The same `<video>` element the WHEP (live) player uses — they share it, never overlap. */
  videoRef: RefObject<HTMLVideoElement | null>;
  /** Advertised window length, used until the real seekable range is known. */
  fallbackWindowSeconds?: number;
}

export interface UseHlsRewindPlayer {
  /**
   * Length of the seekable recall window in seconds. Measured from the live media
   * playlist (sum of its segment durations — MediaMTX cuts segments on the camera's
   * real IDR boundaries, so this is the TRUE rewindable span the server retains);
   * the advertised `fallbackWindowSeconds` only applies until the first playlist load.
   * Whole seconds, and only ever the widest window seen this session, so anything
   * scaled by it (the scrubber) holds still.
   */
  windowSeconds: number;
  /**
   * How far behind the live edge the playhead is, in whole seconds (0 = at live edge),
   * measured against a wall-clock projection of the live edge so a stationary playhead
   * reports a stationary offset.
   */
  offsetFromLiveSeconds: number;
  isPaused: boolean;
  isReady: boolean;
  error: string | null;
  /** Seek to `offsetSeconds` behind the live edge; queues the seek if media isn't ready yet. */
  requestSeekOffsetFromLive: (offsetSeconds: number) => void;
  togglePlay: () => void;
}

const MIN_BACK_BUFFER_SECONDS = 30;
const DEFAULT_WINDOW_SECONDS = 30;
// Segment length assumed until a playlist reports its target duration (native HLS path).
const DEFAULT_SEGMENT_DURATION_SECONDS = 4;
// How far ahead of the last measured edge the projection may run — in segment durations —
// before the playlist counts as stalled and the projection stops advancing.
const MAX_EDGE_PROJECTION_SEGMENTS = 3;
// The offset must move at least this far before a new whole-second value is published,
// so a value resting near x.5 can't flip-flop between neighbouring seconds.
const OFFSET_PUBLISH_DEADBAND_SECONDS = 0.6;
// Fatal networkError events (manifest/level/frag load failed or timed out) mean the HTTP
// request itself didn't succeed — nothing was said about the media content — so retrying
// via hls.startLoad() is safe. Capped so a genuinely dead source (camera offline, gateway
// down) doesn't retry forever instead of surfacing an error. Fatal mediaError is NOT
// retried here: that means bytes were fetched fine but failed to decode/append, which in
// this deployment usually means MediaMTX wrote a corrupt segment (upstream RTP loss) —
// recovering blindly risks looping on the same bad segment.
const MAX_NETWORK_ERROR_RECOVERY_ATTEMPTS = 3;

export const useHlsRewindPlayer = ({
  url,
  enabled,
  videoRef,
  fallbackWindowSeconds,
}: UseHlsRewindPlayerOptions): UseHlsRewindPlayer => {
  const hlsRef = useRef<Hls | null>(null);
  const pendingOffsetRef = useRef<number | null>(null);
  const isReadyRef = useRef(false);
  // Window the server actually retains right now: total duration of the latest
  // media playlist (updated by hls.js on every live playlist reload). Null until
  // the first load, and always null on the native (Safari) path.
  const measuredWindowRef = useRef<number | null>(null);
  // Playlist target duration, i.e. how big a step `seekable.end()` takes per segment.
  const segmentDurationRef = useRef<number | null>(null);
  // Last known live edge plus the wall clock reading it was taken at.
  const liveEdgeAnchorRef = useRef<LiveEdgeAnchor | null>(null);
  // Consecutive fatal networkError recoveries attempted since the last successful
  // fragment load. Reset on FRAG_LOADED so a source that recovers gets a fresh budget.
  const networkErrorRecoveryAttemptsRef = useRef(0);

  const [windowSeconds, setWindowSeconds] = useState(0);
  const [offsetFromLiveSeconds, setOffsetFromLiveSeconds] = useState(0);
  const [isPaused, setIsPaused] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Advertised recall window, used only until the first playlist measurement (and
  // for the native path, which never measures).
  const configuredWindow = Math.max(1, Math.round(fallbackWindowSeconds ?? DEFAULT_WINDOW_SECONDS));

  // Current live edge, projected with wall clock between segment arrivals. Advances the
  // anchor as a side effect, so both the readout and seeks share one notion of "live".
  const readLiveEdge = useCallback((video: HTMLVideoElement): number | null => {
    const { seekable } = video;
    if (!seekable || seekable.length === 0) return null;

    const segmentDuration = segmentDurationRef.current ?? DEFAULT_SEGMENT_DURATION_SECONDS;
    const anchor = projectLiveEdge({
      measuredEdge: seekable.end(seekable.length - 1),
      anchor: liveEdgeAnchorRef.current,
      nowMs: performance.now(),
      maxProjectionSeconds: segmentDuration * MAX_EDGE_PROJECTION_SEGMENTS,
    });
    liveEdgeAnchorRef.current = anchor;
    return anchor.edge;
  }, []);

  // Read the live <video> state and reflect it into React state (guarded to avoid churn).
  const sync = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const liveEdge = readLiveEdge(video);
    if (liveEdge === null) return;

    // The rewindable span is what the playlist still lists, NOT the browser's seekable
    // span: the latter only covers what has been appended so far, so it keeps growing
    // while you watch and would drag the scrubber's scale — and the handle riding on
    // it — along with it. Publish only measured windows (the advertised
    // `configuredWindow` may overstate what the server keeps, and would then be locked
    // in by the widest-seen rule below), so the scale settles once and then holds.
    const measuredWindow = measuredWindowRef.current;
    const win = measuredWindow ?? configuredWindow;
    const offset = Math.min(Math.max(0, liveEdge - video.currentTime), win);

    if (measuredWindow !== null) {
      setWindowSeconds(prev => Math.max(prev, Math.round(measuredWindow)));
    }
    // Whole seconds only: both the readout and the scrubber are second-granular, so
    // finer values would just re-render the controls on every `timeupdate` (~4x/s).
    const nextOffset = Math.round(offset);
    setOffsetFromLiveSeconds(prev =>
      prev !== nextOffset && Math.abs(offset - prev) > OFFSET_PUBLISH_DEADBAND_SECONDS
        ? nextOffset
        : prev
    );
    setIsPaused(prev => (prev !== video.paused ? video.paused : prev));
  }, [videoRef, readLiveEdge, configuredWindow]);

  const requestSeekOffsetFromLive = useCallback(
    (offsetSeconds: number) => {
      const video = videoRef.current;
      if (!video || !isReadyRef.current || video.seekable.length === 0) {
        // Media not ready yet (e.g. entering rewind from live) — apply once it is.
        pendingOffsetRef.current = Math.max(0, offsetSeconds);
        return;
      }
      const start = video.seekable.start(0);
      const end = video.seekable.end(video.seekable.length - 1);
      // Seek against the same projected edge the readout uses, otherwise asking for
      // "10 s behind live" lands 10 s behind the last segment boundary and reads back
      // as more than 10. Clamp to [liveEdge - window, end] using the measured window
      // (and never before the oldest seekable position) so we can't scrub into media
      // the server has evicted, or past the live edge.
      const liveEdge = readLiveEdge(video) ?? end;
      const recallWindow = measuredWindowRef.current ?? configuredWindow;
      const safeOffset = Math.min(Math.max(0, offsetSeconds), recallWindow);
      const earliest = Math.max(start, liveEdge - recallWindow);
      seekVideo(video, Math.min(Math.max(earliest, liveEdge - safeOffset), end));
      void video.play().catch(() => {});
      sync();
    },
    [videoRef, sync, readLiveEdge, configuredWindow]
  );

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) void video.play().catch(() => {});
    else video.pause();
  }, [videoRef]);

  useEffect(() => {
    const video = videoRef.current;
    if (!enabled || !url || !video) return;

    setError(null);
    setIsReady(false);
    setWindowSeconds(0);
    isReadyRef.current = false;
    networkErrorRecoveryAttemptsRef.current = 0;

    const onReady = () => {
      if (!isReadyRef.current) {
        isReadyRef.current = true;
        setIsReady(true);
        const pending = pendingOffsetRef.current;
        pendingOffsetRef.current = null;
        if (pending != null) requestSeekOffsetFromLive(pending);
      }
      sync();
    };

    video.addEventListener('loadedmetadata', onReady);
    video.addEventListener('canplay', onReady);
    video.addEventListener('timeupdate', sync);
    video.addEventListener('seeked', sync);
    video.addEventListener('progress', sync);
    video.addEventListener('play', sync);
    video.addEventListener('pause', sync);

    let hls: Hls | null = null;
    if (Hls.isSupported()) {
      // Preferred path (Chrome/Edge/etc.): hls.js + MSE. This MUST be checked before
      // native HLS — some Chromium builds return a non-empty canPlayType('application/
      // vnd.apple.mpegurl') ('maybe') yet cannot actually play HLS natively, which would
      // otherwise send us down the native branch and thrash the <video> element.
      hls = new Hls({
        lowLatencyMode: false, // recall path only; live stays WebRTC (server uses fmp4 variant)
        backBufferLength: Math.max(
          MIN_BACK_BUFFER_SECONDS,
          fallbackWindowSeconds ?? MIN_BACK_BUFFER_SECONDS
        ),
        // Publish the whole playlist window as the seekable range instead of just what
        // has been appended, so the scrubber's full span is reachable from the moment
        // rewind starts (hls.js fetches the older segments on seek) and its scale doesn't
        // have to grow as the buffer fills.
        liveDurationInfinity: true,
      });
      hlsRef.current = hls;
      // A fragment landing means the source has recovered — give the next fatal
      // networkError a fresh recovery budget instead of counting it against a blip
      // that already resolved itself.
      hls.on(Hls.Events.FRAG_LOADED, () => {
        networkErrorRecoveryAttemptsRef.current = 0;
      });
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data.fatal) return;
        if (
          data.type === Hls.ErrorTypes.NETWORK_ERROR &&
          networkErrorRecoveryAttemptsRef.current < MAX_NETWORK_ERROR_RECOVERY_ATTEMPTS
        ) {
          networkErrorRecoveryAttemptsRef.current += 1;
          hls?.startLoad();
          return;
        }
        setError(data.details ?? 'HLS error');
      });
      // Every live playlist reload reports the window the server retains right now:
      // the sum of the playlist's segment durations (true, IDR-aligned lengths) —
      // not the deployment's advertised hlsSegmentCount x hlsSegmentDuration.
      hls.on(Hls.Events.LEVEL_UPDATED, (_event, data) => {
        measuredWindowRef.current = data.details.totalduration;
        segmentDurationRef.current = data.details.levelTargetDuration;
        sync();
      });
      hls.loadSource(url);
      hls.attachMedia(video);
      void video.play().catch(() => {});
    } else if (video.canPlayType('application/vnd.apple.mpegurl') !== '') {
      // Native HLS (Safari) — HEVC decoded by the platform. Fallback only.
      setVideoSource(video, url);
      void video.play().catch(() => {});
    } else {
      setError('HLS playback not supported in this browser');
    }

    return () => {
      video.removeEventListener('loadedmetadata', onReady);
      video.removeEventListener('canplay', onReady);
      video.removeEventListener('timeupdate', sync);
      video.removeEventListener('seeked', sync);
      video.removeEventListener('progress', sync);
      video.removeEventListener('play', sync);
      video.removeEventListener('pause', sync);
      hls?.destroy();
      hlsRef.current = null;
      isReadyRef.current = false;
      measuredWindowRef.current = null;
      segmentDurationRef.current = null;
      liveEdgeAnchorRef.current = null;
      // Release the element so the live (WHEP) player can reclaim it cleanly.
      try {
        video.pause();
        video.removeAttribute('src');
        video.load();
      } catch {
        /* ignore */
      }
    };
  }, [enabled, url, videoRef, fallbackWindowSeconds, sync, requestSeekOffsetFromLive]);

  // Reset transient state when leaving rewind so the controls don't show stale values.
  useEffect(() => {
    if (!enabled) {
      pendingOffsetRef.current = null;
      setIsReady(false);
      setWindowSeconds(0);
      setOffsetFromLiveSeconds(0);
      setIsPaused(true);
    }
  }, [enabled]);

  return {
    windowSeconds,
    offsetFromLiveSeconds,
    isPaused,
    isReady,
    error,
    requestSeekOffsetFromLive,
    togglePlay,
  };
};

//*********************************
// Helpers
//*********************************
// Writes to the shared <video> go through these. Driving that element is this hook's job,
// but it arrives as `videoRef` — a hook argument, which React Compiler treats as frozen —
// so assigning to its properties inline reads as mutating an argument.
const seekVideo = (video: HTMLVideoElement, time: number) => {
  video.currentTime = time;
};

const setVideoSource = (video: HTMLVideoElement, url: string) => {
  video.src = url;
};

type LiveEdgeAnchor = {
  edge: number;
  atMs: number;
};

type ProjectLiveEdgeArgs = {
  /** `seekable.end()`, i.e. the live edge as last reported by the browser. */
  measuredEdge: number;
  anchor: LiveEdgeAnchor | null;
  nowMs: number;
  /** How far ahead of `measuredEdge` the projection may run before it holds. */
  maxProjectionSeconds: number;
};

/**
 * `seekable.end()` only moves when a whole segment is appended, so it under-reports the
 * live edge by up to one segment duration and then jumps — which made a playhead sitting
 * a fixed distance behind live look like it was sliding back and forth. Media time and
 * wall clock run at the same rate, so project the last anchored edge forward instead, and
 * take the measurement whenever it overtakes the projection (a segment landed). Capped so
 * a stalled playlist can't let the projection run away from the media.
 */
const projectLiveEdge = ({
  measuredEdge,
  anchor,
  nowMs,
  maxProjectionSeconds,
}: ProjectLiveEdgeArgs): LiveEdgeAnchor => {
  if (!anchor) return { edge: measuredEdge, atMs: nowMs };

  const projected = anchor.edge + (nowMs - anchor.atMs) / 1000;
  const edge = Math.min(Math.max(measuredEdge, projected), measuredEdge + maxProjectionSeconds);
  return { edge, atMs: nowMs };
};
